import { useMemo, useState } from 'react';
import {
  Search,
  LayoutGrid,
  List,
  SlidersHorizontal,
  BadgeCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type {
  CoverageItem,
  MediaClass,
  MediaItemStatus,
  MediaKind,
  MediaSentiment,
  SocialPlatform,
  SocialPostFormat,
  SourceAccount,
} from '@/client/types/media';
import {
  MEDIA_CLASS_BADGE,
  MEDIA_SENTIMENT_BADGE,
  MEDIA_STATUS_BADGE,
  PLATFORM_LABEL,
  PUBLISHABLE_PLATFORMS,
  SOCIAL_FORMAT_LABEL,
  publicMetrics,
} from '@/client/types/media';
import { formatCompact } from '../fixtures';
import {
  publishedRangeParams,
  useAdaptedAccounts,
  useApiTrackedKeywords,
  useCoverageItems,
} from '../coverage-api';
import { useBulkTriageMutation } from '@/client/api/marketing/radar';
import { getApiErrorMessage } from '@/client/utils/errors';
import { CoverageDetailDrawer } from './CoverageDetailDrawer';
import { PlatformLogoBadge, normalizeHandle } from './PlatformLogo';
import { SocialPlatformPreview } from './SocialPlatformPreview';
import { KeywordTrackerPanel } from './KeywordTrackerPanel';
import { matchingKeywords } from '../keyword-match';
import { INSIGHT_RANGE_DAYS, type InsightRangeDays } from '@/client/pages/Admin/Marketing/posting-insights-data';

const PLATFORMS: SocialPlatform[] = [...PUBLISHABLE_PLATFORMS];

type ViewMode = 'feed' | 'table';
type SortKey = 'urgency' | 'published' | 'engagement' | 'reach';
type AccountScope = 'all' | 'owned' | 'tracked';

interface SocialCoverageViewProps {
  kind: MediaKind | MediaKind[];
  defaultStatus?: MediaItemStatus | 'all';
  /** Show keyword/hashtag tracker + filter (Mentions). */
  enableKeywordTracking?: boolean;
}

interface AccountOption {
  account: SourceAccount;
  count: number;
}

function matchesKind(item: CoverageItem, kind: MediaKind | MediaKind[]) {
  return Array.isArray(kind) ? kind.includes(item.kind) : item.kind === kind;
}

function engagementScore(item: CoverageItem) {
  const m = publicMetrics(item);
  return (
    (m.likes ?? 0) +
    (m.comments ?? 0) * 2 +
    (m.shares ?? 0) * 3 +
    (m.saves ?? 0) * 2
  );
}

function publicReach(item: CoverageItem): number {
  const m = publicMetrics(item);
  return m.reach ?? m.views ?? 0;
}

function resolveAccountId(
  item: CoverageItem,
  accountList: SourceAccount[],
): string | undefined {
  if (item.social?.accountId) {
    // Prefer id if still present in store
    if (accountList.some((a) => a.id === item.social?.accountId)) {
      return item.social.accountId;
    }
  }
  if (!item.platform || !item.social?.authorHandle) return undefined;
  const handle = normalizeHandle(item.social.authorHandle);
  return accountList.find(
    (a) => a.platform === item.platform && normalizeHandle(a.handle) === handle,
  )?.id;
}

function accountInitials(name: string) {
  return name
    .replace(/@/g, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function SocialCoverageView({
  kind,
  defaultStatus = 'all',
  enableKeywordTracking = false,
}: SocialCoverageViewProps) {
  const [rangeDays, setRangeDays] = useState<InsightRangeDays>(90);
  const windowParams = useMemo(() => publishedRangeParams(rangeDays), [rangeDays]);
  const coverage = useCoverageItems({
    channel: 'social',
    ...windowParams,
  });
  const items = coverage.items;
  const accounts = useAdaptedAccounts();
  const bulkTriage = useBulkTriageMutation();
  const { keywords: trackedKeywords } = useApiTrackedKeywords();
  const channelKeywords = useMemo(
    () => trackedKeywords.filter((k) => k.channels.includes('social')),
    [trackedKeywords],
  );
  const [q, setQ] = useState('');
  const [view, setView] = useState<ViewMode>('feed');
  const [status, setStatus] = useState<MediaItemStatus | 'all'>(defaultStatus);
  const [platforms, setPlatforms] = useState<Set<SocialPlatform>>(new Set());
  const [accountIds, setAccountIds] = useState<Set<string>>(new Set());
  const [keywordIds, setKeywordIds] = useState<Set<string>>(new Set());
  const [accountScope, setAccountScope] = useState<AccountScope>('all');
  const [mediaClass, setMediaClass] = useState<MediaClass | 'all'>('all');
  const [sentiment, setSentiment] = useState<MediaSentiment | 'all'>('all');
  const [format, setFormat] = useState<SocialPostFormat | 'all'>('all');
  const [minEngRate, setMinEngRate] = useState<number>(0);
  const [sort, setSort] = useState<SortKey>('urgency');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState<CoverageItem | null>(null);

  const socialBase = useMemo(
    () =>
      items.filter(
        (i) => i.channel === 'social' && matchesKind(i, kind) && i.platform && i.social,
      ),
    [items, kind],
  );

  const platformCounts = useMemo(() => {
    const counts: Partial<Record<SocialPlatform, number>> = {};
    for (const item of socialBase) {
      if (!item.platform) continue;
      counts[item.platform] = (counts[item.platform] ?? 0) + 1;
    }
    return counts;
  }, [socialBase]);

  /** Accounts that appear in the current social base, optionally narrowed by selected platforms. */
  const accountOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of socialBase) {
      if (platforms.size > 0 && item.platform && !platforms.has(item.platform)) continue;
      const id = resolveAccountId(item, accounts);
      if (!id) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }

    const opts: AccountOption[] = [];
    for (const [id, count] of counts) {
      const account = accounts.find((a) => a.id === id);
      if (!account) continue;
      if (accountScope === 'owned' && !account.owned) continue;
      if (accountScope === 'tracked' && account.owned) continue;
      opts.push({ account, count });
    }

    // Show newly added accounts (0 coverage) when a platform filter is active
    for (const account of accounts) {
      if (platforms.size > 0 && !platforms.has(account.platform)) continue;
      if (accountScope === 'owned' && !account.owned) continue;
      if (accountScope === 'tracked' && account.owned) continue;
      if (opts.some((o) => o.account.id === account.id)) continue;
      if (platforms.size > 0) opts.push({ account, count: 0 });
    }

    return opts.sort((a, b) => {
      if (a.account.owned !== b.account.owned) return a.account.owned ? -1 : 1;
      if (a.account.platform !== b.account.platform) {
        return PLATFORMS.indexOf(a.account.platform) - PLATFORMS.indexOf(b.account.platform);
      }
      return b.count - a.count;
    });
  }, [socialBase, platforms, accountScope, accounts]);

  /** Group account chips under each platform for clearer sub-filtering. */
  const accountsByPlatform = useMemo(() => {
    const groups: { platform: SocialPlatform; options: AccountOption[] }[] = [];
    for (const p of PLATFORMS) {
      const options = accountOptions.filter((o) => o.account.platform === p);
      if (options.length) groups.push({ platform: p, options });
    }
    return groups;
  }, [accountOptions]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = socialBase
      .filter((item) => (status === 'all' ? true : item.status === status))
      .filter((item) => (platforms.size === 0 ? true : item.platform && platforms.has(item.platform)))
      .filter((item) => {
        if (accountIds.size === 0) return true;
        const id = resolveAccountId(item, accounts);
        return id != null && accountIds.has(id);
      })
      .filter((item) => {
        if (!enableKeywordTracking || keywordIds.size === 0) return true;
        const selected = channelKeywords.filter((k) => keywordIds.has(k.id) && k.enabled);
        if (selected.length === 0) return true;
        // Item must match at least one selected tracker
        return matchingKeywords(item, selected).length > 0;
      })
      .filter((item) => (mediaClass === 'all' ? true : item.mediaClass === mediaClass))
      .filter((item) => (sentiment === 'all' ? true : item.sentiment === sentiment))
      .filter((item) => (format === 'all' ? true : item.social?.format === format))
      .filter((item) => {
        if (minEngRate <= 0) return true;
        const er = publicMetrics(item).engagementRate;
        return er != null && er >= minEngRate;
      })
      .filter((item) => {
        if (!query) return true;
        const hay = [
          item.title,
          item.summary,
          item.author,
          item.social?.caption,
          item.social?.authorHandle,
          ...(item.tags ?? []),
          ...(item.social?.hashtags ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(query);
      });

    list = [...list].sort((a, b) => {
      if (sort === 'published') return b.publishedAt.localeCompare(a.publishedAt);
      if (sort === 'engagement') return engagementScore(b) - engagementScore(a);
      if (sort === 'reach') return publicReach(b) - publicReach(a);
      return (b.urgencyScore ?? -1) - (a.urgencyScore ?? -1);
    });

    return list;
  }, [
    socialBase,
    status,
    platforms,
    accountIds,
    accounts,
    enableKeywordTracking,
    keywordIds,
    channelKeywords,
    mediaClass,
    sentiment,
    format,
    minEngRate,
    q,
    sort,
  ]);

  const togglePlatform = (p: SocialPlatform) => {
    setPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);

      setAccountIds((prevAcc) => {
        if (prevAcc.size === 0 || next.size === 0) return prevAcc;
        const allowed = new Set(
          accounts.filter((a) => next.has(a.platform)).map((a) => a.id),
        );
        return new Set([...prevAcc].filter((id) => allowed.has(id)));
      });

      return next;
    });
  };

  const toggleAccount = (id: string) => {
    setAccountIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAccountsOnPlatform = (platform: SocialPlatform) => {
    const ids = accountOptions
      .filter((o) => o.account.platform === platform)
      .map((o) => o.account.id);
    setAccountIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
    setPlatforms((prev) => new Set(prev).add(platform));
  };

  const bulk = (next: MediaItemStatus) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulkTriage.mutate(
      { ids, status: next },
      {
        // modified, not requested: they differ when something was already in
        // that state, and the request count would overstate the work.
        onSuccess: (res) =>
          toast.success(`${res?.data?.modified ?? 0} of ${ids.length} moved to ${next}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not triage the selection')),
      },
    );
    setSelected(new Set());
  };

  const liveItems = filtered.map((f) => items.find((i) => i.id === f.id) ?? f);

  return (
    <div className="space-y-4">
      {enableKeywordTracking && (
        <KeywordTrackerPanel
          items={socialBase}
          selectedIds={keywordIds}
          onSelectedIdsChange={setKeywordIds}
          channel="social"
        />
      )}

      {/* Platform chips with logos */}
      <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Platforms
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setPlatforms(new Set());
              setAccountIds(new Set());
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              platforms.size === 0
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            All · {socialBase.length}
          </button>
          {PLATFORMS.map((p) => {
            const count = platformCounts[p] ?? 0;
            if (count === 0) return null;
            const activeChip = platforms.has(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePlatform(p)}
                className={`inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-xs font-medium transition ${
                  activeChip
                    ? 'border-blue-300 bg-blue-50 text-blue-900 ring-2 ring-blue-200 ring-offset-1'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <PlatformLogoBadge platform={p} size="sm" />
                <span>{PLATFORM_LABEL[p]}</span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Account sub-filters grouped by platform */}
      <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Accounts
            {platforms.size > 0 && (
              <span className="ml-1 font-normal normal-case text-gray-400">
                · filtered to selected platforms
              </span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-1">
            {(['all', 'owned', 'tracked'] as AccountScope[]).map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => setAccountScope(scope)}
                className={`rounded-md px-2 py-1 text-[11px] font-medium capitalize ${
                  accountScope === scope
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {scope === 'all' ? 'All accounts' : scope}
              </button>
            ))}
            {accountIds.size > 0 && (
              <button
                type="button"
                onClick={() => setAccountIds(new Set())}
                className="ml-1 text-[11px] font-medium text-blue-700 hover:underline"
              >
                Clear accounts
              </button>
            )}
          </div>
        </div>

        {accountsByPlatform.length === 0 ? (
          <p className="text-sm text-gray-500">No accounts match the current platform filters.</p>
        ) : (
          <div className="space-y-3">
            {accountsByPlatform.map(({ platform, options }) => (
              <div key={platform}>
                <div className="mb-1.5 flex items-center gap-2">
                  <PlatformLogoBadge platform={platform} size="sm" />
                  <span className="text-xs font-semibold text-gray-800">
                    {PLATFORM_LABEL[platform]}
                  </span>
                  <button
                    type="button"
                    onClick={() => selectAllAccountsOnPlatform(platform)}
                    className="text-[11px] font-medium text-blue-700 hover:underline"
                  >
                    Toggle all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {options.map(({ account, count }) => {
                    const activeAcc = accountIds.has(account.id);
                    return (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() => toggleAccount(account.id)}
                        className={`inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-2.5 text-left text-xs transition ${
                          activeAcc
                            ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200 ring-offset-1'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <span className="relative">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-[10px] font-semibold text-white">
                            {accountInitials(account.displayName)}
                          </span>
                          <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-[1px] shadow">
                            <PlatformLogoBadge platform={account.platform} size="sm" />
                          </span>
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-1 font-medium text-gray-900">
                            {account.displayName}
                            {account.verified && (
                              <BadgeCheck className="h-3 w-3 text-sky-500" />
                            )}
                          </span>
                          <span className="block truncate text-[10px] text-gray-500">
                            {account.handle}
                            {account.owned ? ' · owned' : ' · tracked'} · {count}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search caption, handle, hashtag, account…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as MediaItemStatus | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="flagged">Flagged</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={String(rangeDays)}
          onChange={(e) => setRangeDays(Number(e.target.value) as InsightRangeDays)}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
          title="Server-side published_at window"
        >
          {INSIGHT_RANGE_DAYS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <select
          value={mediaClass}
          onChange={(e) => setMediaClass(e.target.value as MediaClass | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All classes</option>
          <option value="owned">Owned</option>
          <option value="earned">Earned</option>
          <option value="shared">Shared / UGC</option>
          <option value="paid">Paid</option>
        </select>
        <select
          value={sentiment}
          onChange={(e) => setSentiment(e.target.value as MediaSentiment | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All sentiment</option>
          <option value="positive">Positive</option>
          <option value="neutral">Neutral</option>
          <option value="negative">Negative</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="urgency">Sort: urgency</option>
          <option value="published">Sort: newest</option>
          <option value="engagement">Sort: engagement</option>
          <option value="reach">Sort: reach</option>
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          More
        </Button>
        <div className="ml-auto flex rounded-md border border-gray-200 p-0.5">
          <button
            type="button"
            onClick={() => setView('feed')}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium ${
              view === 'feed' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Feed
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium ${
              view === 'table' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List className="h-3.5 w-3.5" /> Table
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-gray-500">Format</span>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as SocialPostFormat | 'all')}
              className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm"
            >
              <option value="all">All formats</option>
              {Object.entries(SOCIAL_FORMAT_LABEL).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-gray-500">Min eng. rate</span>
            <select
              value={String(minEngRate)}
              onChange={(e) => setMinEngRate(Number(e.target.value))}
              className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm"
            >
              <option value="0">Any</option>
              <option value="0.02">≥ 2%</option>
              <option value="0.05">≥ 5%</option>
              <option value="0.08">≥ 8%</option>
            </select>
          </label>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setPlatforms(new Set());
              setAccountIds(new Set());
              setAccountScope('all');
              setMediaClass('all');
              setSentiment('all');
              setFormat('all');
              setMinEngRate(0);
              setStatus(defaultStatus);
              setQ('');
            }}
          >
            Reset filters
          </Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span className="font-medium">{selected.size} selected</span>
          <Button size="sm" variant="secondary" onClick={() => bulk('reviewed')}>
            Mark reviewed
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('flagged')}>
            Flag
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('archived')}>
            Archive
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Showing <span className="font-medium text-gray-800">{liveItems.length}</span> of{' '}
        {socialBase.length} social items
        {accountIds.size > 0 && (
          <span>
            {' '}
            · {accountIds.size} account{accountIds.size === 1 ? '' : 's'} selected
          </span>
        )}
        {enableKeywordTracking && keywordIds.size > 0 && (
          <span>
            {' '}
            · {keywordIds.size} keyword tracker{keywordIds.size === 1 ? '' : 's'}
          </span>
        )}
      </p>

      {liveItems.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white py-16 text-center text-sm text-gray-500 shadow-sm">
          No social items match these filters.
        </div>
      )}

      {view === 'feed' && liveItems.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {liveItems.map((item) => (
            <div key={item.id} className="relative">
              <div className="absolute left-3 top-3 z-10">
                <input
                  type="checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(item.id)) next.delete(item.id);
                      else next.add(item.id);
                      return next;
                    });
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                  aria-label={`Select ${item.title}`}
                />
              </div>
              <button
                type="button"
                className="w-full text-left transition hover:opacity-95"
                onClick={() => setActive(item)}
              >
                <SocialPlatformPreview item={item} />
              </button>
              <div className="mt-2 flex flex-wrap items-center gap-1 px-1">
                {item.platform && <PlatformLogoBadge platform={item.platform} size="sm" />}
                {enableKeywordTracking &&
                  matchingKeywords(
                    item,
                    channelKeywords.filter((k) => k.enabled),
                  )
                    .slice(0, 3)
                    .map((kw) => (
                      <span
                        key={kw.id}
                        className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-800"
                      >
                        {kw.label}
                      </span>
                    ))}
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_STATUS_BADGE[item.status]}`}
                >
                  {item.status}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_SENTIMENT_BADGE[item.sentiment]}`}
                >
                  {item.sentiment}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_CLASS_BADGE[item.mediaClass]}`}
                >
                  {item.mediaClass}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  urg {item.urgencyScore ?? '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'table' && liveItems.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-3" />
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Post
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Account
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Tracking
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {liveItems.map((item) => {
                  const accId = resolveAccountId(item, accounts);
                  const acc = accounts.find((a) => a.id === accId);
                  return (
                    <tr
                      key={item.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setActive(item)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          onChange={() => {
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(item.id)) next.delete(item.id);
                              else next.add(item.id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-3">
                          <div
                            className={`h-12 w-12 shrink-0 rounded-md ${item.social?.mediaTone ?? 'bg-gray-200'}`}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">
                              {item.title}
                            </p>
                            <p className="mt-0.5 line-clamp-1 text-xs text-gray-400">
                              {item.social?.caption ?? item.summary}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {item.platform && (
                            <PlatformLogoBadge platform={item.platform} size="sm" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {acc?.displayName ?? item.social?.authorDisplayName}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {acc?.handle ?? item.social?.authorHandle}
                              {acc?.owned ? ' · owned' : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">
                        <div>
                          Reach {formatCompact(publicReach(item) || undefined)}
                        </div>
                        <div>
                          ER{' '}
                          {publicMetrics(item).engagementRate != null
                            ? `${(publicMetrics(item).engagementRate! * 100).toFixed(1)}%`
                            : '—'}{' '}
                          · ❤️ {formatCompact(publicMetrics(item).likes)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${MEDIA_STATUS_BADGE[item.status]}`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CoverageDetailDrawer
        item={active ? items.find((i) => i.id === active.id) ?? active : null}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </div>
  );
}
